import { useDroppable } from '@dnd-kit/core';
import { Alert, Box, Paper, Typography } from '@mui/material';
import React from 'react';
import type { DayOfWeek, Lesson } from '../../types';
import { LessonCard } from './LessonCard';

interface PeriodSlotProps {
  day: DayOfWeek;
  period: number;
  lessons: Lesson[];
  isEditable?: boolean;
  conflicts?: string[];
}

export const PeriodSlot: React.FC<PeriodSlotProps> = ({
  day,
  period,
  lessons,
  isEditable = true,
  conflicts = [],
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `${day}-${period}`,
    data: {
      day,
      period,
    },
    disabled: !isEditable,
  });

  const hasConflict = conflicts.length > 0;

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 1,
        minHeight: 100,
        backgroundColor: isOver ? 'action.hover' : hasConflict ? 'error.lighter' : 'background.paper',
        border: hasConflict ? '2px solid' : '1px solid',
        borderColor: hasConflict ? 'error.main' : 'divider',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {lessons.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 80,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {isEditable ? 'Drop lesson here' : 'No lesson'}
          </Typography>
        </Box>
      ) : (
        <>
          {lessons.map((lesson) => (
            <LessonCard key={lesson.id} lesson={lesson} isEditable={isEditable} />
          ))}
          {hasConflict && (
            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} detected
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};
